import axios from 'axios';
import {
	useApi,
	Res,
	useCacheInvalidator,
	useCacheWildcardInvalidator,
	isTransient,
	reportTemporaryFailure,
} from './api';
import { useConfig } from '../config';
import { ApiActionResponse, ApiProduct } from '../types';
import { useEffect, useState } from 'react';
import { filterDuplicateProducts } from '../components/utils';
import { cookieService } from '../components/cookieService';
import { tagPlausibleCustomer } from '../hooks/useAnalytics';

// read by the plausible inline script in the html template, which tags a visit as an existing
// customer's when the sign-in it records is old enough. written by ConsentMode, since it is
// analytics state and follows the statistics consent; it survives the session expiring, so that a
// customer coming back after the week a session lasts still counts as one. only an explicit
// logout removes it. see plausibleCustomerTagScript
export const CUSTOMER_INFO_COOKIE = 'customerInfo';

export function useSession() {
	let { data, isLoading, error } = useApi(Res.session, {});
	const invalidate = useCacheInvalidator();
	const invalidateWildcard = useCacheWildcardInvalidator();
	const { api, locale } = useConfig();
	const {
		data: specialsWithProducts,
		error: specialProductsError,
		isLoading: specialProducsIsLoading,
	} = useApi(Res.specialsWithProducts, {});
	const [specialsProducts, setSpecialsProducts] = useState<ApiProduct[]>([]);
	const [specialsProductIds, setSpecialsProductsIds] = useState<number[]>([]);

	/*
	 * Counted, not yet acted on. `isLoggedIn` below cannot tell "you are not signed in" from "we
	 * could not ask", and answers false to both - so a briefly unavailable session renders a
	 * signed-in customer as a stranger, with the prices that go with it. Same for the specials:
	 * an empty list is indistinguishable from a list we failed to fetch.
	 */
	useEffect(() => {
		// only when there is nothing to fall back on: SWR keeps the last good session
		// through a failed revalidation, and isLoggedIn is still right in that case
		if (data === undefined && isTransient(error)) {
			reportTemporaryFailure(
				{
					url: error.url,
					status: error.status,
					kind: 'read-as-answer',
					resource: 'session',
				},
				error
			);
		}
	}, [error, data]);

	useEffect(() => {
		if (specialsWithProducts === undefined && isTransient(specialProductsError)) {
			reportTemporaryFailure(
				{
					url: specialProductsError.url,
					status: specialProductsError.status,
					kind: 'read-as-answer',
					resource: 'specials',
				},
				specialProductsError
			);
		}
	}, [specialProductsError, specialsWithProducts]);

	useEffect(() => {
		if (specialsWithProducts) {
			let specialsProducts: ApiProduct[] = specialsWithProducts
				.sort((a, b) => a.product.name.localeCompare(b.product.name, locale, { sensitivity: 'base' }))
				.map((specialProduct) => ({
					...specialProduct.product,
					price: specialProduct.newPrice,
				}));
			specialsProducts = filterDuplicateProducts(specialsProducts);
			setSpecialsProductsIds(specialsProducts.map((product) => product.id));
			setSpecialsProducts(specialsProducts);
		}
	}, [specialsWithProducts]);

	function mutatePrivateResources() {
		invalidateWildcard('cart.json');
		invalidate(Res.favorites, {});
		invalidate(Res.favoriteIds, {});
		invalidate(Res.session, {});
		invalidate(Res.bonusBalance, {});
		invalidate(Res.customer, {});
		invalidate(Res.invoiceAddresses, {});
		invalidate(Res.checkoutState, {});
	}

	return {
		isLoggedIn: !!data,
		isLoading,
		user: data && data !== true ? data : undefined,
		specialsProducts,
		specialProductsError,
		specialProducsIsLoading,
		specialsProductIds,
		async logout(): Promise<ApiActionResponse> {
			const result = await axios.post<ApiActionResponse>(`${api}me/logout.json`, null, { withCredentials: true });
			// the one thing that ends a customer's marker, so it happens on any host: the cookie is
			// shared with staging, and a logout there must end the marker for production too. some
			// callers reload right after, so it has to happen here and now, or the next document's
			// first pageview is still tagged existing; others navigate within the app, so the tag on
			// this document is corrected as well
			if (result.data.success) {
				cookieService.remove(CUSTOMER_INFO_COOKIE, { siteWide: true });
				tagPlausibleCustomer('new');
			}
			mutatePrivateResources();
			return result.data;
		},
		async login(email: string, password: string): Promise<ApiActionResponse> {
			const loginApiEndpoint = `${api}me/login.json`;
			const loginResponse = await axios.post<ApiActionResponse>(
				loginApiEndpoint,
				{ email, password },
				{ withCredentials: true }
			);
			mutatePrivateResources();
			return loginResponse.data;
		},
	};
}
